import { ComponentRef } from '@angular/core';
import { Exclude, Expose, Transform, Type } from 'class-transformer';
import { TimeBlockService } from '../../../features/calendar/components/time-block/services/time-block.service';
import { TimeBlockComponent } from '../../../features/calendar/components/time-block/time-block.component';
import {
  FixInterval,
  ITimeBlockViewType,
  TimeBlockType,
} from '../../../features/calendar/components/time-block/types/time-block-types';
import { DateTimeHelper } from '../../../features/calendar/util/date-time-helper';
import { EntityType } from '../../../shared/data-types/entity-types';
import { TimeBlockSource } from '../../../shared/data-types/shared-time-block-types';
import { AbsenceReasonModel } from '../absence/absence-reason.model';

export class TimeBlockModel<T = TimeBlockComponent> {
  @Exclude({ toPlainOnly: true })
  public id = -1;

  // The "real" start date of the whole time block compound.
  @Transform(
    ({ value, obj }) =>
      obj.fixInterval === null || obj.fixInterval === undefined
        ? DateTimeHelper.dateToISO8601String(value, false)
        : DateTimeHelper.dateToISO8601String(value, true),
    { toPlainOnly: true },
  )
  @Transform(({ value, obj }) => TimeBlockService.skipSecondsForIncomingTimeBlock(value, obj), {
    toClassOnly: true,
  })
  public start = new Date();

  // The "real" end date of the whole time block compound.
  @Transform(
    ({ value, obj }) =>
      obj.fixInterval === null || obj.fixInterval === undefined
        ? DateTimeHelper.dateToISO8601String(value, false)
        : DateTimeHelper.dateToISO8601String(value, true),
    { toPlainOnly: true },
  )
  @Transform(({ value, obj }) => TimeBlockService.skipSecondsForIncomingTimeBlock(value, obj), {
    toClassOnly: true,
  })
  public end = new Date();

  public description = '';

  // TimeBlockDayOrWeekVariableIntervalType, TimeBlockDayOrWeekFixIntervalType,
  // TimeBlockMonthVariableIntervalType, TimeBlockMonthFixIntervalType
  @Exclude()
  public timeBlockViewType: ITimeBlockViewType | null = null;

  @Exclude({ toPlainOnly: true })
  public source: TimeBlockSource | null = null;

  @Exclude()
  public componentRef: ComponentRef<T> | null = null;

  @Expose({ name: 'is_active' })
  public isActive = false;

  @Expose({ name: 'fix_interval' })
  public fixInterval: FixInterval | null = null;

  @Expose({ name: 'user_id' })
  public userId: number | null = null;

  @Exclude()
  public type: TimeBlockType = TimeBlockType.ExistingBlock;

  // Indicates the part of a composed time block that exceeds a day or a week.
  // For month view, partNumber = 0 is the first visible part, but there could be a previous invisible time block part if the composed
  // time block starts before the current month.
  @Exclude()
  public partNumber = 0;

  // Indicates the number of parts the time block consists. Each day represents a part.
  @Exclude()
  public partCount = 1;
}

// /// Time block model for project type
export class TimeBlockProjectModel<T = TimeBlockComponent> extends TimeBlockModel<T> {
  @Exclude({ toPlainOnly: true })
  @Expose({ name: 'task' })
  public task: EntityType = {
    id: -1,
    name: '',
  };

  @Exclude({ toPlainOnly: true })
  @Expose({ name: 'project' })
  public project: EntityType & { color: string } = {
    id: -1,
    name: '',
    color: '',
  };

  @Exclude({ toPlainOnly: true })
  @Expose({ name: 'client' })
  public client: EntityType = {
    id: -1,
    name: '',
  };

  @Exclude({ toClassOnly: true })
  @Expose({ name: 'invoice_id' })
  public invoiceId = null;

  // For create and update operations, just pass the id.
  @Expose({ name: 'task_id' })
  public getTask(): number {
    return +this.task.id!;
  }
}

// /// Time block model for absence type
export class TimeBlockAbsenceModel<T = TimeBlockComponent> extends TimeBlockModel<T> {
  @Exclude({ toPlainOnly: true })
  @Type(() => AbsenceReasonModel)
  @Expose({ name: 'reason' })
  public reason: AbsenceReasonModel = {
    id: -1,
    name: '',
    isWorkingTime: true,
  };

  @Expose({ name: 'reason' })
  public getReason(): number {
    return +this.reason.id!;
  }
}

// /// Time block model for appointment type
export class TimeBlockAppointmentModel<T = TimeBlockComponent> extends TimeBlockModel<T> {
  public name = '';
}

// Time block model for slots
export class SlotTimeBlockModel extends TimeBlockModel {}
